import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { FaCheck, FaStar, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import PlanModal from './PlanModal';

const PricingPlansSection = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState(null);

  useEffect(() => {
    // Use translation keys instead of loading from JSON
    const translatedPlans = [
      {
        id: 'starter',
        name: t('pricing.plans.starter.name'),
        price: t('pricing.plans.starter.price'),
        description: t('pricing.plans.starter.description'),
        popular: false,
        features: [
          t('pricing.plans.starter.feature1'),
          t('pricing.plans.starter.feature2'),
          t('pricing.plans.starter.feature3'),
          t('pricing.plans.starter.feature4'),
          t('pricing.plans.starter.feature5'),
          t('pricing.plans.starter.feature6'),
          t('pricing.plans.starter.feature7'),
          t('pricing.plans.starter.feature8'),
          t('pricing.plans.starter.feature9'),
          t('pricing.plans.starter.feature10'),
          t('pricing.plans.starter.feature11'),
          t('pricing.plans.starter.feature12'),
          t('pricing.plans.starter.feature13')
        ]
      },
      {
        id: 'professional',
        name: t('pricing.plans.professional.name'),
        price: t('pricing.plans.professional.price'),
        description: t('pricing.plans.professional.description'),
        popular: true,
        features: [
          t('pricing.plans.professional.feature1'),
          t('pricing.plans.professional.feature2'),
          t('pricing.plans.professional.feature3'),
          t('pricing.plans.professional.feature4'),
          t('pricing.plans.professional.feature5'),
          t('pricing.plans.professional.feature6'),
          t('pricing.plans.professional.feature7'),
          t('pricing.plans.professional.feature8'),
          t('pricing.plans.professional.feature9'),
          t('pricing.plans.professional.feature10'),
          t('pricing.plans.professional.feature11'),
          t('pricing.plans.professional.feature12'),
          t('pricing.plans.professional.feature13'),
          t('pricing.plans.professional.feature14'),
          t('pricing.plans.professional.feature15')
        ]
      },
      {
        id: 'premium',
        name: t('pricing.plans.premium.name'),
        price: t('pricing.plans.premium.price'),
        description: t('pricing.plans.premium.description'),
        popular: false,
        features: [
          t('pricing.plans.premium.feature1'),
          t('pricing.plans.premium.feature2'),
          t('pricing.plans.premium.feature3'),
          t('pricing.plans.premium.feature4'),
          t('pricing.plans.premium.feature5'),
          t('pricing.plans.premium.feature6'),
          t('pricing.plans.premium.feature7'),
          t('pricing.plans.premium.feature8'),
          t('pricing.plans.premium.feature9'),
          t('pricing.plans.premium.feature10'),
          t('pricing.plans.premium.feature11'),
          t('pricing.plans.premium.feature12'),
          t('pricing.plans.premium.feature13'),
          t('pricing.plans.premium.feature14'),
          t('pricing.plans.premium.feature15')
        ]
      },
      {
        id: 'corporate',
        name: t('pricing.plans.corporate.name'),
        price: t('pricing.plans.corporate.price'),
        description: t('pricing.plans.corporate.description'),
        popular: false,
        badge: 'Enterprise',
        features: [
          t('pricing.plans.corporate.feature1'),
          t('pricing.plans.corporate.feature2'),
          t('pricing.plans.corporate.feature3'),
          t('pricing.plans.corporate.feature4'),
          t('pricing.plans.corporate.feature5'),
          t('pricing.plans.corporate.feature6'),
          t('pricing.plans.corporate.feature7'),
          t('pricing.plans.corporate.feature8'),
          t('pricing.plans.corporate.feature9'),
          t('pricing.plans.corporate.feature10'),
          t('pricing.plans.corporate.feature11'),
          t('pricing.plans.corporate.feature12'),
          t('pricing.plans.corporate.feature13')
        ]
      }
    ];

    const translatedAddons = [
      {
        id: 'camera',
        name: t('pricing.addons.camera.name'),
        price: '€299',
        description: t('pricing.addons.camera.description')
      },
      {
        id: 'doorbell',
        name: t('pricing.addons.doorbell.name'),
        price: '€199',
        description: t('pricing.addons.doorbell.description')
      },
      {
        id: 'thermostat',
        name: t('pricing.addons.thermostat.name'),
        price: '€249',
        description: t('pricing.addons.thermostat.description')
      },
      {
        id: 'lights',
        name: t('pricing.addons.lights.name'),
        price: '€149',
        description: t('pricing.addons.lights.description')
      },
      {
        id: 'sensor',
        name: t('pricing.addons.sensor.name'),
        price: '€99',
        description: t('pricing.addons.sensor.description')
      },
      {
        id: 'lock',
        name: t('pricing.addons.lock.name'),
        price: '€349',
        description: t('pricing.addons.lock.description')
      },
      {
        id: 'speaker',
        name: t('pricing.addons.speaker.name'),
        price: '€179',
        description: t('pricing.addons.speaker.description')
      },
      {
        id: 'blinds',
        name: t('pricing.addons.blinds.name'),
        price: '€399',
        description: t('pricing.addons.blinds.description')
      },
      {
        id: 'panel',
        name: t('pricing.addons.panel.name'),
        price: '€249',
        description: t('pricing.addons.panel.description')
      },
      {
        id: 'plug',
        name: t('pricing.addons.plug.name'),
        price: '€79',
        description: t('pricing.addons.plug.description')
      },
      {
        id: 'extender',
        name: t('pricing.addons.extender.name'),
        price: '€129',
        description: t('pricing.addons.extender.description')
      }
    ];

    setPlans(translatedPlans);
    setAddons(translatedAddons);
  }, [t]);

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setIsModalOpen(true);
  };

  const toggleExpand = (planId) => {
    setExpandedPlan(expandedPlan === planId ? null : planId);
  };

  return (
    <section
      id="pricing"
      ref={ref}
      className="section-padding bg-gradient-to-b from-[#0a0e27] to-[#0f1640] relative overflow-hidden"
      data-testid="pricing-section"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-[#00d4ff]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-[#0099ff]/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <div className="inline-block glass px-6 py-2 rounded-full mb-6">
            <span className="text-[#00d4ff] font-semibold uppercase tracking-wider text-sm">
              {t('pricing.badge')}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('pricing.title')}
            <span className="gradient-text block mt-2">{t('pricing.subtitle')}</span>
          </h2>
          <p className="text-white/70 text-lg max-w-3xl mx-auto">
            {t('pricing.description')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {plans.map((plan, index) => {
            const isExpanded = expandedPlan === plan.id;
            const displayFeatures = isExpanded ? plan.features : plan.features.slice(0, 5);
            const hasMore = plan.features.length > 5;

            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.15 }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="relative flex flex-col"
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                    <div className="bg-gradient-to-r from-[#00d4ff] to-[#0099ff] text-white px-6 py-1 rounded-full text-sm font-semibold flex items-center space-x-1">
                      <FaStar className="text-yellow-300" />
                      <span>{t('pricing.popular')}</span>
                    </div>
                  </div>
                )}
                
                {plan.badge && !plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                    <div className="bg-gradient-to-r from-purple-500 to-purple-700 text-white px-6 py-1 rounded-full text-sm font-semibold">
                      {plan.badge}
                    </div>
                  </div>
                )}

                <div 
                  className={`glass-strong rounded-3xl p-8 flex flex-col h-full relative overflow-hidden ${
                    plan.popular ? 'ring-2 ring-[#00d4ff] ring-offset-2 ring-offset-[#0a0e27]' : ''
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute inset-0 bg-gradient-to-br from-[#00d4ff]/10 to-[#0099ff]/10 pointer-events-none" />
                  )}

                  <div className="relative z-10 flex-1 flex flex-col">
                    <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                    <p className="text-white/60 text-sm mb-6">{plan.description}</p>

                    <div className="mb-8">
                      <div className={`font-bold gradient-text mb-2 ${plan.id === 'corporate' ? 'text-3xl' : 'text-5xl'}`}>{plan.price}</div>
                      {plan.id !== 'corporate' && (
                        <div className="text-white/50 text-sm">{t('pricing.oneTime')}</div>
                      )}
                    </div>

                    <div className="space-y-3 mb-6 flex-1">
                      {displayFeatures.map((feature, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <div className="w-5 h-5 rounded-full bg-[#00d4ff]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <FaCheck className="text-[#00d4ff] text-xs" />
                          </div>
                          <span className="text-white/80 text-sm leading-relaxed">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {hasMore && (
                      <button
                        onClick={() => toggleExpand(plan.id)}
                        className="flex items-center justify-center space-x-2 text-[#00d4ff] hover:text-white transition-colors mb-6 text-sm font-medium"
                      >
                        <span>{isExpanded ? t('pricing.showLess') : t('pricing.showMore')}</span>
                        {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                      </button>
                    )}

                    <button
                      onClick={() => handleSelectPlan(plan)}
                      className={`w-full py-3 px-6 rounded-xl font-semibold transition-all mt-auto ${
                        plan.popular
                          ? 'bg-gradient-to-r from-[#00d4ff] to-[#0099ff] text-white hover:shadow-lg hover:shadow-[#00d4ff]/50'
                          : 'glass text-white hover:bg-white/10'
                      }`}
                    >
                      {plan.id === 'corporate' ? t('pricing.requestQuote') : t('pricing.selectPlan')}
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
          className="text-center mt-12"
        >
          <p className="text-white/60 text-sm">
            {t('pricing.customNote')}
          </p>
        </motion.div>
      </div>

      {selectedPlan && (
        <PlanModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          plan={selectedPlan}
          addons={addons}
        />
      )}
    </section>
  );
};

export default PricingPlansSection;
